import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import Input from '~/bootcamp/Activities/components/Input'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import { RectangleWithSlice } from '~/bootcamp/Activities/Fractions/components/RectangleWithSlice/RectangleWithSlice'
import { CircleWithSlice } from '~/bootcamp/Activities/Fractions/components/CircleWithSlice/CircleWithSlice'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

const MIN_NUMERATORS = [1, 1, 1, 1, 1]
const MAX_NUMERATORS = [4, 4, 4, 6, 9]
const MAX_DENOMINATORS = [5, 5, 5, 7, 10]
const MAX_HISTORY_LENGTH = [3, 3, 3, 4, 5]
const IS_SLICES_TOGETHER = [true, true, true, false, false]

function FractionFigures({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC: SubmitButton,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState({ denominator: '', numerator: '' })
  const [numeratorHistory, setNumeratorHistory] = useState<number[]>([])
  const [{ denominator, numerator }, setNumbers] = useState({
    denominator: 1,
    numerator: 1,
  })
  const [isRectangle, setIsRectangle] = useState(true)
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    const generatedNumbers = generateDenominatorNumerator(
      MIN_NUMERATORS[difficultyLevel - 1],
      MAX_NUMERATORS[difficultyLevel - 1],
      2,
      MAX_DENOMINATORS[difficultyLevel - 1],
      numeratorHistory,
    )
    setNumbers(generatedNumbers)
    setIsRectangle(Math.random() < 0.5)
    setAnswer({ denominator: '', numerator: '' })
  }, [numeratorHistory, difficultyLevel])

  // Every level asks for the whole fraction, so both boxes are empty and both numbers ride the guarded channel.
  useEmitInstance(
    onInstanceChange,
    denominator > 1 ? 'a shaded figure; \\frac{\\square}{\\square}' : null,
    denominator > 1 ? `\\frac{${numerator}}{${denominator}}` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer.denominator === denominator.toString() && answer.numerator === numerator.toString()
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer({ denominator: '', numerator: '' })
  }

  const handleOnNextQuestionClick = (): void => {
    setNumeratorHistory((prevState) => {
      return [numerator, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1])
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer.denominator !== '' && answer.numerator !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="">
        {isRectangle ? (
          <RectangleWithSlice
            className="w-28"
            denominator={denominator}
            isRandomFill={!IS_SLICES_TOGETHER[difficultyLevel - 1]}
            numerator={numerator}
          />
        ) : (
          <CircleWithSlice
            isRandomFill={!IS_SLICES_TOGETHER[difficultyLevel - 1]}
            numOfDenominator={denominator}
            numOfNumerator={numerator}
          />
        )}
        <div className="ml-4 flex flex-col items-center">
          <Input
            aria-label={t('ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={`${inputKey}-first`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: state.denominator, numerator: value }))}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer.numerator}
          />
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: value, numerator: state.numerator }))}
            onEnter={onEnterPress}
            value={answer.denominator}
          />
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid={answer.denominator !== '' && answer.numerator !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FractionFigures
